import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { usePersistentState, consumeNavigationPreserve, clearNamespace } from "../../hooks/persistence";
import { CommonService } from "../../Service/CommonService";
import { InventoryApiService } from "../../Service/InventoryApiService";
import "../Dynamic/InventoryCSV.scss";
import { VisibilityOutlined } from "@mui/icons-material";
import StackDetails from "./StackDetails";
import WarehouseOffCanvas from "./WarehouseOffCanvas";
import { Form } from "react-bootstrap";
import ExpandMore from '../../assets/images/icons/ExpandMore.svg';
import DataGridLoader from "../Common/DataGridLoader";

const WarehouseLocations: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [isLoading, setIsLoading] = useState(false);
  const localeText = {
    noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
  };
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const params =
    id === undefined && CommonService.isUser()
      ? CommonService.getCompanyId()
      : id;
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [showPalletDetails, setShowPalletDetails] = useState(false);
  const [selectedPalletId, setSelectedPalletId] = useState();
  const [selectedLots, setSelectedLots] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [debounceFilter, setDebounceFilter] = useState(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const initialFilter = {
    age: null,
    lotNo: null,
    barrelOwnerName: null,
    companyName: null,
    producer: null,
    dsp: null,
    fillDate: null,
    barrelNumber: null,
    mashBill: null,
    palletId: null,
    row: null,
    stack: null,
    level: null,
    stagingArea: null,
    barrelCount: null,
    distilleries: null,
    warehouse: null,
    owner: null,
    createdAt: null
  };

  const _filterInitRef = useRef(false);
  if (!_filterInitRef.current) {
    _filterInitRef.current = true;
    if (!consumeNavigationPreserve("warehouse-locations")) {
      clearNamespace("warehouse-locations");
    }
  }

  const [activeButton, setActiveButton] = usePersistentState<string>("warehouse-locations", "activeTab", "pallets");
  const activeButtonRef = useRef(activeButton);
  const [sortField, setSortField] = usePersistentState<string>("warehouse-locations", "sortField", "");
  const [sortDirection, setSortDirection] = usePersistentState<string>("warehouse-locations", "sortDirection", "asc");
  const [selectedWarehouse, setSelectedWarehouse] = usePersistentState<string>("warehouse-locations", "selectedWarehouse", "");
  const [selectedWarehouseName, setSelectedWarehouseName] = usePersistentState<string>("warehouse-locations", "selectedWarehouseName", "");
  const [page, setPage] = usePersistentState<number>("warehouse-locations", "page", 0);
  const [pageSize, setPageSize] = usePersistentState<number>("warehouse-locations", "pageSize", 50);
  const [filter, setFilter] = usePersistentState<any>("warehouse-locations", "filter", initialFilter);

  const warehouses = [
    { value: "WDTS WH1", label: "WDTS WH1" },
    { value: "WDTS WH2", label: "WDTS WH2" },
    { value: "WDTS WH3", label: "WDTS WH3" },
    { value: "WDTS WH4", label: "WDTS WH4" }
  ];

   useEffect(() => {
    
      const handleClickOutside = (event: MouseEvent) => {
        if (
          divRef.current &&
          !divRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)
        ) {
          setShowDropdown(false);
        }
      };
    
    
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        
      };
  }, [showDropdown]); // Only run when dropdown is toggled
  

  const fetchWarehouseDropdown = async () => {
    try {
        const response = await InventoryApiService.getWarehouseDropdown();
        setWarehouseOptions(response.data);

        const restoredWarehouseId = (location.state as any)?.selectedWarehouseId;
        const restoredWarehouseName = (location.state as any)?.selectedWarehouseName;

        let activeWarehouseId = response.data[0]?.warehouseId;
        let activeWarehouseName = response.data[0]?.warehouseName;

        if (restoredWarehouseId && response.data.some((w: any) => w.warehouseId === restoredWarehouseId)) {
            activeWarehouseId = restoredWarehouseId;
            activeWarehouseName = restoredWarehouseName;
        }

        setSelectedWarehouse(activeWarehouseId);
        setSelectedWarehouseName(activeWarehouseName);

        const openOffCanvas = (location.state as any)?.openOffCanvas;
        const offCanvasType = (location.state as any)?.offCanvasType;
        const restoredPalletId = (location.state as any)?.selectedPalletId;

        if (openOffCanvas && offCanvasType === "pallet" && restoredPalletId) {
            setSelectedPalletId(restoredPalletId);
            setShowPalletDetails(true);
        }

        // update history state with current values
        history.replace({
            ...history.location,
            state: {
                ...(history.location.state as any || {}),
                selectedWarehouseId: activeWarehouseId,
                selectedWarehouseName: activeWarehouseName,
                activeButton: activeButtonRef.current
            }
        });

        //now fetch the details for selected Warehouse
        if (activeButtonRef.current === "pallets") {
            fetchPalletListing(activeWarehouseId, page, pageSize, sortField, sortDirection, filter);
        } else if (activeButtonRef.current === "barrels") {
            fetchBarrelListing(activeWarehouseId, page, pageSize, sortField, sortDirection, filter);
        }

    } catch (error) {
        console.log("error while calling warehouse dropdown api", error);
    }
  };

  const fetchWarehouseDetails = async (warehouseId:any) => {

    if(activeButtonRef.current == 'pallets') {
        fetchPalletListing(warehouseId, page, pageSize, sortField, sortDirection, filter);
    } else if(activeButtonRef.current == 'barrels') {
        fetchBarrelListing(warehouseId, page, pageSize, sortField, sortDirection, filter);
    }
  }

  const triggerDelayedApi = (tempFilter: any) => {
    setDebounceFilter(tempFilter);
  };

  useEffect(() => {
    if (!debounceFilter) return;

    const timeout = setTimeout(() => {
      if (activeButtonRef.current === "pallets") {
        fetchPalletListing(
          selectedWarehouse,
          page,
          pageSize,
          sortField,
          sortDirection,
          debounceFilter
        );
      } else {
        fetchBarrelListing(
          selectedWarehouse,
          page,
          pageSize,
          sortField,
          sortDirection,
          debounceFilter
        );
      }
    }, 600); // Delay

    return () => clearTimeout(timeout);
  }, [debounceFilter]);


  useEffect(() => {
    fetchWarehouseDropdown();
  }, []);

  const fetchPalletListing = async (warehouseId:any, page: any, size: any, sortField: any, sortDirection: any, filter: any) => {
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      const payload = {
        ...filter,
        warehouse: Array.isArray(filter?.warehouse)
          ? filter.warehouse.join(",")
          : filter?.warehouse
      };

      setIsLoading(true);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      const response = await InventoryApiService.fetchAllPalletsByWarehouseId(warehouseId, page, size, sortField, sortDirection, payload, signal);
      setTableData(Object.values(response.data.content)); // Convert object to array
      setTotalCount(response.data.totalElements);
      setIsLoading(false);
    } catch(error) {
        console.log("error occurred while api calling " + error)
    }
  }

  const fetchBarrelListing = async (warehouseId:any, page: any, size: any, sortField: any, sortDirection: any, filter: any) => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    try {
      const payload = {
        ...filter,
        warehouse: Array.isArray(filter?.warehouse)
          ? filter.warehouse.join(",")
          : filter?.warehouse
      };

      setIsLoading(true);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      const response = await InventoryApiService.getAllWarehouseBarrel(warehouseId, page, size, sortField, sortDirection, payload, signal);
      setTableData(Object.values(response.data.content)); // Convert object to array
      setTotalCount(response.data.totalElements);
      setIsLoading(false);
    } catch(error) {
        console.log("error occurred while api calling " + error)
    } 
  }

  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString() || ""}>
        <span>{value?.toString() || "-"}</span>
      </Tooltip>
    );
  };

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  }

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof any
  ) => {
    const value = event.target.value;
    const trimmedValue = value?.trim();

    // Prepare a temporary filter object with the updated field value
    const tempFilter = {
      ...filter, // Spread existing filter state
      [field]: trimmedValue === "" ? null : trimmedValue, // Update the specific field
    };

    // Update the filter state
    setFilter(tempFilter);

    triggerDelayedApi(tempFilter);
  };
  
  const handleWarehouseOptionChange = (event: any, value: string, warehouses: string[] | null) => {
    const isChecked = event.target.checked;

    const warehouseArray = warehouses || [];

    const updatedWarehouseArray = isChecked
      ? [...warehouseArray, value]
      : warehouseArray.filter((s) => s !== value);

    setFilter({
      ...filter,
      warehouse: updatedWarehouseArray.length > 0 ? updatedWarehouseArray : null
    });

    // Convert to comma-string only when sending api
    triggerDelayedApi({
      ...filter,
      warehouse: updatedWarehouseArray.length > 0 
        ? updatedWarehouseArray.join(",") 
        : null
    });
  };

  const barrelColumns: GridColDef[] = [
    {
      field: "age",
      headerName: "Age",
      flex: 1,
      minWidth: 200,
      sortable: false,
      valueGetter: (value: any, row: any) => {
        const currentDate = new Date();
        const fillDate = new Date(row.fillDate); // Use the fillDate field

        // Calculate the difference in years and months
        let years = currentDate.getFullYear() - fillDate.getFullYear();
        let months = currentDate.getMonth() - fillDate.getMonth();

        // Adjust for negative months (if current month is earlier than the fill month)
        if (months < 0) {
          years--;
          months += 12;
        }

        // Format the result as 'X years Y months'
        return `${years} years ${months} months`;
      },
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "170px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.age || ""}
              // defaultValue={filter.age}
              onChange={(event) => handleFilterChange(event, "age")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "warehouse",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",

      renderHeader: (params) => (
        <>
          <div>
            <Typography
              variant="body2"
              sx={{ color: "#6F6F6F", fontSize: "14px" }}
            >
              {params.colDef.headerName}
            </Typography>

            <div className="position-relative" style={{ width: "140px" }}>
              <div
                className="form-control mb-1"
                style={{
                  cursor: "pointer",
                  borderBottomColor: "#D1D1D1",
                  marginTop: "4px",
                  background: "#fff",
                  padding: "5px",
                  overflowX: "hidden",
                  paddingRight: "16px",
                  textOverflow: "ellipsis",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDropdown();
                }}
                ref={buttonRef}
              >
                {filter.warehouse?.length > 0
                  ? filter.warehouse?.join(", ")
                  : "Select Warehouse"}
                <img
                  src={ExpandMore}
                  className="position-absolute top-50 end-0 translate-middle"
                />
              </div>
              {showDropdown && (
                <div
                  className="dropdown-menu show status-multiselect-dropdown"
                  onClick={(event) => event.stopPropagation()}
                  style={{width:"140px", minWidth:"140px", maxWidth:"140px"}}
                  ref={divRef}
                >
                  {warehouses.map((option) => (
                    <Form.Check
                      key={option.value}
                      id={`status-${option.value}`}
                      type="checkbox"
                      label={option.label}
                      value={option.value}
                      checked={filter?.warehouse?.includes(option.value) || false}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleWarehouseOptionChange(event, option.value, filter.warehouse);
                      }}
                      className="cursor-pointer"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ),
    },
    {
      field: "lotNo",
      headerName: "Lot ID",
      flex: 1,
      minWidth: 200,
      sortable: false,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "150px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.lotNo || ""}
              // defaultValue={filter.lotNo}
              onChange={(event) => handleFilterChange(event, "lotNo")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "barrelNumber",
      headerName: "Container Id",
      flex: 1,
      sortable: false,
      minWidth: 230,
      renderCell: (params) => <CellWithTooltip value={params.value} />,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "170px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              //value={filter.title}
              defaultValue={filter.barrelNumber}
              onChange={(event) => handleFilterChange(event, "barrelNumber")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "barrelOwnerName",
      headerName: "Owner Name",
      flex: 1,
      sortable: false,
      minWidth: 230,
      renderCell: (params) => <CellWithTooltip value={params.value} />,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "170px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              //value={filter.title}
              defaultValue={filter.barrelOwnerName}
              onChange={(event) => handleFilterChange(event, "barrelOwnerName")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "distillery",
      headerName: "Distillery",
      flex: 1,
      sortable: false,
      minWidth: 200,
      renderCell: (params) => <CellWithTooltip value={params.value} />,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "170px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.distillery || ""}
              // defaultValue={filter.distillery}
              onChange={(event) => handleFilterChange(event, "distillery")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "row",
      headerName: "Row",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.row || ""}
              // defaultValue={filter.row}
              onChange={(event) => handleFilterChange(event, "row")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "stack",
      headerName: "Stack",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.stack || ""}
              // defaultValue={filter.stack}
              onChange={(event) => handleFilterChange(event, "stack")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "level",
      headerName: "Level",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.level || ""}
              // defaultValue={filter.level}
              onChange={(event) => handleFilterChange(event, "level")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "stagingArea",
      headerName: "Staging Area",
      flex: 1,
      minWidth: 200,
      sortable: false,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "150px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.stagingArea || ""}
              // defaultValue={filter.lotNo}
              onChange={(event) => handleFilterChange(event, "stagingArea")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "mashBill",
      headerName: "Mash Bill",
      flex: 1,
      minWidth: 230,
      sortable: false,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "170px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.mashBill || ""}
              // defaultValue={filter.mashBill}
              onChange={(event) => handleFilterChange(event, "mashBill")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      headerClassName: "table-header border-0 my-2 h-100",
      minWidth: 230,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => <CellWithTooltip value={params.value} />,
      valueGetter: (value: any, row: any) => {
        const dateTimeString = row.createdAt ? row.createdAt : null;
        if (dateTimeString) {
          return CommonService.formatDateTime(dateTimeString);
        }
      },
      renderHeader: (params) => (
        <div style={{ width: "165px" }}>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <input
            type="date"
            className="form-control border mt-1"
            style={{ boxShadow: "none" }}
            id="createdAt"
            name="createdAt"
            placeholder="Select Date"
            //value={filter.createdDate}
            defaultValue={filter.createdAt}
            onChange={(event) => handleFilterChange(event, "createdAt")}
            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
          />
        </div>
      ),
    },
  ];

  const palletColumns: GridColDef[] = [
    {
      field: "palletId",
      headerName: "Pallet #",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.palletId || ""  }
              // defaultValue={filter.palletId}
              onChange={(event) => handleFilterChange(event, "palletId")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "warehouse",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <>
          <div>
            <Typography
              variant="body2"
              sx={{ color: "#6F6F6F", fontSize: "14px" }}
            >
              {params.colDef.headerName}
            </Typography>

            <div className="position-relative" style={{ width: "140px" }}>
              <div
                className="form-control mb-1"
                style={{
                  cursor: "pointer",
                  borderBottomColor: "#D1D1D1",
                  marginTop: "4px",
                  background: "#fff",
                  padding: "5px",
                  overflowX: "hidden",
                  paddingRight: "16px",
                  textOverflow: "ellipsis",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDropdown();
                }}
                ref={buttonRef}
              >
                {filter.warehouse?.length > 0
                  ? filter.warehouse?.join(", ")
                  : "Select Warehouse"}
                <img
                  src={ExpandMore}
                  className="position-absolute top-50 end-0 translate-middle"
                />
              </div>
              {showDropdown && (
                <div
                  className="dropdown-menu show status-multiselect-dropdown"
                  onClick={(event) => event.stopPropagation()}
                  style={{width:"140px", minWidth:"140px", maxWidth:"140px"}}
                  ref={divRef}
                >
                  {warehouses.map((option) => (
                    <Form.Check
                      key={option.value}
                      id={`status-${option.value}`}
                      type="checkbox"
                      label={option.label}
                      value={option.value}
                      checked={filter?.warehouse?.includes(option.value) || false}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleWarehouseOptionChange(event, option.value, filter.warehouse);
                      }}
                      className="cursor-pointer"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ),
    },
    {
      field: "row",
      headerName: "Row",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.row || ""}
              // defaultValue={filter.row}
              onChange={(event) => handleFilterChange(event, "row")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "stack",
      headerName: "Stack",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.stack || ""}
              // defaultValue={filter.stack}
              onChange={(event) => handleFilterChange(event, "stack")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "level",
      headerName: "Level",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.level || ""}
              // defaultValue={filter.level}
              onChange={(event) => handleFilterChange(event, "level")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "stagingArea",
      headerName: "Staging Area",
      flex: 1,
      minWidth: 200,
      sortable: false,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "150px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.stagingArea || ""}
              onChange={(event) => handleFilterChange(event, "stagingArea")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "barrelCount",
      headerName: "Container Count",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "130px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.barrelCount || ""}
              // defaultValue={filter.barrelCount}
              onChange={(event) => handleFilterChange(event, "barrelCount")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value} />,
    },
    {
      field: "distilleries",
      headerName: "Distilleries",
      flex: 1,
      sortable: false,
      minWidth: 200,
      renderCell: (params) => <CellWithTooltip value={params.value} />,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "150px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.distilleries || ""}
              // defaultValue={filter.distillery}
              onChange={(event) => handleFilterChange(event, "distilleries")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "lotNo",
      headerName: "Lots",
      flex: 1,
      sortable: false,
      minWidth: 220,
      // renderCell: (params) => <CellWithTooltip value={params.value} />,
      renderCell: (params) => {
        const lots = params.value ? params.value.split(",").map(s => s.trim()) : [];
        const first = lots[0] || "";
        const extraCount = lots.length - 1;

        const handleClick = (event) => {
          event.stopPropagation();
          setSelectedLots(lots.slice(1)); // exclude the first one
          setAnchorEl(event.currentTarget);
        };

        return (
          <span style={{ cursor: "pointer" }}>
            {first}
            {extraCount > 0 && (
              <span 
                onClick={handleClick}
                style={{ color: "#1976d2", marginLeft: "4px" }}
              >
                +{extraCount} more
              </span>
            )}
          </span>
        );
      },
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "160px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.lotNo || ""}
              // defaultValue={filter.lotNo}
              onChange={(event) => handleFilterChange(event, "lotNo")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "owner",
      headerName: "Owners",
      flex: 1,
      sortable: false,
      minWidth: 220,
      // renderCell: (params) => <CellWithTooltip value={params.value} />,
      renderCell: (params) => {
        const owners = params.value ? params.value.split(",").map(s => s.trim()) : [];
        const first = owners[0] || "";
        const extraCount = owners.length - 1;

        const handleClick = (event) => {
          event.stopPropagation();
          setSelectedLots(owners.slice(1)); // exclude the first one
          setAnchorEl(event.currentTarget);
        };

        return (
          <span style={{ cursor: "pointer" }}>
            {first}
            {extraCount > 0 && (
              <span 
                onClick={handleClick}
                style={{ color: "#1976d2", marginLeft: "4px" }}
              >
                +{extraCount} more
              </span>
            )}
          </span>
        );
      },
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "160px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.owner || ""}
              // defaultValue={filter.owner}
              onChange={(event) => handleFilterChange(event, "owner")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      headerClassName: "table-header border-0 my-2 h-100",
      minWidth: 220,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => <CellWithTooltip value={params.value} />,
      valueGetter: (value: any, row: any) => {
        const dateTimeString = row.createdAt ? row.createdAt : null;
        if (dateTimeString) {
          return CommonService.formatDateTime(dateTimeString);
        }
      },
      renderHeader: (params) => (
        <div style={{ width: "160px" }}>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <input
            type="date"
            className="form-control border mt-1"
            style={{ boxShadow: "none" }}
            id="createdAt"
            name="createdAt"
            placeholder="Select Date"
            //value={filter.createdDate}
            defaultValue={filter.createdAt}
            onChange={(event) => handleFilterChange(event, "createdAt")}
            onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
          />
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      width: 100,
      headerClassName: "table-header border-0 my-2 h-100",
      renderCell: (params: any) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View Pallet Details" arrow>
            <IconButton
              aria-label="edit"
              size="small"
              onClick={() => {
                setShowPalletDetails(true);
                setSelectedPalletId(params?.row?.id)
              }}
            >
              <VisibilityOutlined />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  function routeBack() {
      history.goBack();
  }

  const tableDataWithIds = tableData.map((rowData, index) => ({
    ...rowData,
  }));

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    if(activeButtonRef.current == "pallets") {
        fetchPalletListing(selectedWarehouse, newPaginationModel.page, newPaginationModel.pageSize, sortField, sortDirection, filter)
    } else {
        fetchBarrelListing(selectedWarehouse, newPaginationModel.page, newPaginationModel.pageSize, sortField, sortDirection, filter)
    }
  };

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      if(activeButtonRef.current == "pallets") {
        fetchPalletListing(selectedWarehouse, page, pageSize, newSortField, newSortDirection, filter);
      } else {
        fetchBarrelListing(selectedWarehouse, page, pageSize, newSortField, newSortDirection, filter);
      }
    }
  };

  return (
    <div
      className={`usersList usersList__user ${!CommonService.isAdmin()? "userBarrelList" : "barrelList"}`}
      style={{ paddingBottom: CommonService.isAdmin() ? 47 : "" }}
    >
      <div className="usersList__header--wrapper d-flex flex-wrap align-items-center w-100">
        <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2 position-relative">
            
          <div className="d-flex align-items-center gap-2 flex-wrap left-wrapper">
              
                {CommonService.isAdmin() && (
                  <>
                    <div className="back__button" onClick={routeBack}>
                    
                        <IconButton
                            aria-label="ArrowBackIcon"
                            sx={{ backgroundColor: "#FFF"}}
                        >
                            <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                        </IconButton>
                    </div>

                    <div className="header--title">
                        <h1 className="title mb-0">
                            Warehouses 
                        </h1>
                    </div>
                    {activeButton == 'stacks' && (<div className="divider mx-2"></div>)}   
                 </>
                )}
                
            {activeButton == 'stacks' && (<div>
              <select
                className="form-select"
                value={selectedWarehouse}
                onChange={(e) => {
                    const whId = e.target.value; // warehouseId
                    const whName = e.target.options[e.target.selectedIndex].text;
                    setSelectedWarehouse(whId);
                    setSelectedWarehouseName(whName)
                    fetchWarehouseDetails(whId);
                    history.replace({
                      ...history.location,
                      state: {
                        ...(history.location.state as any || {}),
                        selectedWarehouseId: whId,
                        selectedWarehouseName: whName
                      }
                    });
                }}
                >
                {warehouseOptions.map((option) => (
                    <option key={option.warehouseId} value={option.warehouseId}>
                    {option.warehouseName}
                    </option>
                ))}
              </select>

            </div>)}
          </div>

          <div className="ms-auto">
            <div
              className="btn-group user-activity-btn-grp flex-wrap"
              role="group"
              aria-label="Basic mixed styles example"
            >
              <button
                className={`tab-btn d-inline-flex justify-content-between align-items-center
                    ${
                      activeButtonRef.current === "pallets"
                        ? "bg-theme"
                        : "bg-theme-white"
                    }`}
                onClick={() => {
                  activeButtonRef.current = "pallets";
                  setActiveButton("pallets");
                  setShowPalletDetails(false);
                  history.replace({
                    ...history.location,
                    state: {
                      ...(history.location.state as any || {}),
                      activeButton: "pallets",
                      openOffCanvas: false
                    }
                  });
                  const updatedFilter = {
                    age: null,
                    lotNo: null,
                    barrelOwnerName: null,
                    companyName: null,
                    producer: null,
                    dsp: null,
                    fillDate: null,
                    barrelNumber: null,
                    mashBill: null,
                    palletId: null,
                    row: null,
                    stack: null,
                    level: null,
                    barrelCount: null,
                    distilleries: null,
                    owner: null,
                    createdAt: null
                  };
                  setFilter(updatedFilter)
                  fetchPalletListing(selectedWarehouse, page, pageSize, "palletId", sortDirection, updatedFilter)
                }}
              >
                Pallets
              </button>

              <button
                className={`tab-btn d-inline-flex justify-content-between align-items-center
                    ${
                      activeButtonRef.current === "stacks"
                        ? "bg-theme"
                      : "bg-theme-white"
                    }`}
                onClick={() => {
                  activeButtonRef.current = "stacks";
                  setActiveButton("stacks");
                  history.replace({
                    ...history.location,
                    state: {
                      ...(history.location.state as any || {}),
                      activeButton: "stacks",
                      openOffCanvas: false
                    }
                  });
                }}
              >
                Stacks
              </button>

              <button
                className={`tab-btn d-inline-flex justify-content-between align-items-center ${activeButtonRef.current === "barrels" ? "bg-theme" : "bg-theme-white"}`}
                onClick={() => {
                  activeButtonRef.current = "barrels";
                  setActiveButton("barrels");
                  setShowPalletDetails(false);
                  history.replace({
                    ...history.location,
                    state: {
                      ...(history.location.state as any || {}),
                      activeButton: "barrels",
                      openOffCanvas: false
                    }
                  });
                  const updatedFilter = {
                    age: null,
                    lotNo: null,
                    barrelOwnerName: null,
                    companyName: null,
                    producer: null,
                    dsp: null,
                    fillDate: null,
                    barrelNumber: null,
                    mashBill: null,
                    palletId: null,
                    row: null,
                    stack: null,
                    level: null,
                    barrelCount: null,
                    distilleries: null,
                    owner: null,
                    createdAt: null
                  };
                  setFilter(updatedFilter)
                  fetchBarrelListing(selectedWarehouse, page, pageSize, "createdAt", sortDirection, updatedFilter)

                }}
              >
                Containers
              </button>
            </div>
            </div>
            <div className="blank-div text-end">
          </div>
        </div>
      </div>
      <div className="user-table-container position-relative">

        {activeButton == "stacks" ? (
          <StackDetails warehouseId= {selectedWarehouse} warehouseName= {selectedWarehouseName} />
        ): (
          <>
          <div className="userTable">
          <DataGrid
            rows={tableDataWithIds}
            columns={activeButtonRef.current === "pallets" ? palletColumns : barrelColumns}
            className="data-grid data-grid__myinventories support-req--listing"
            initialState={{
              pagination: {
                paginationModel: { page, pageSize },
              },
            }}
            pagination
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onSortModelChange={handleSortChange}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={[10, 20, 50, 100]}
            rowCount={totalCount}
            disableRowSelectionOnClick={true}
            loading={isLoading}
            slots={{
              loadingOverlay: DataGridLoader,
            }}
            disableColumnResize
            sx={{
              "& .MuiDataGrid-columnHeaderCheckbox": {
                borderBottom: "unset !important"
              },
            }}
          />
          </div>

          {showPalletDetails && (
            <WarehouseOffCanvas 
              type="pallet" 
              palletId= {selectedPalletId}  
              onClose={() => {
                setShowPalletDetails(false);
                history.replace({
                  ...history.location,
                  state: {
                    ...(history.location.state as any || {}),
                    openOffCanvas: false
                  }
                });
              }}
            />
          )}
          </>
        )}

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          className="warehouse-table-popover"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: { padding: "10px", minWidth: "120px" }
          }}
        >
          {selectedLots.map((lot, index) => (
            <Typography key={index} sx={{ paddingBottom: "4px", fontSize: "14px" }}>
              {lot}
            </Typography>
          ))}
        </Popover>

 
      </div>
    </div>
  );
};

export default WarehouseLocations;